import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Mic2, PlayCircle, Radio, Sparkles } from "lucide-react";

export const AsoVideoBriefing = ({ report, onComplete }) => {
  const [lineIndex, setLineIndex] = useState(0);
  const totalLines = report.video_brief.script_lines.length;
  const progress = ((lineIndex + 1) / totalLines) * 100;
  const audioBars = [28, 46, 24, 54, 38, 52, 34, 48, 22, 44];

  useEffect(() => {
    if (!report?.video_brief?.script_lines?.length) return undefined;
    if (lineIndex >= report.video_brief.script_lines.length - 1) {
      const doneTimer = window.setTimeout(() => onComplete?.(), 1400);
      return () => window.clearTimeout(doneTimer);
    }

    const timer = window.setTimeout(() => setLineIndex((current) => current + 1), 2200);
    return () => window.clearTimeout(timer);
  }, [lineIndex, report, onComplete]);

  const metricCards = useMemo(() => [
    { label: "ASO Score", value: `${Math.round(report.dashboard_summary.aso_score.value)}/100`, source: "Estimated score" },
    { label: "Store Rating", value: `${report.app_rating.toFixed(1)}★`, source: "Live store data" },
    { label: "Growth Potential", value: `${report.growth_potential_percent}%`, source: "Estimated score" },
    { label: "Sentiment", value: report.sentiment_label, source: "Estimated score" },
  ], [report]);

  const briefingSources = ["MOCKED video", "Live store inputs", "Estimated narration"]; 

  return (
    <section className="px-6 pb-12 pt-4 sm:px-8 lg:px-12" data-testid="aso-video-briefing-section">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.6rem] border border-white/[0.08] bg-black/25 shadow-[0_30px_120px_rgba(0,0,0,0.4)]">
        <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">
          <div className="relative min-h-[560px] overflow-hidden border-b border-white/[0.08] bg-[radial-gradient(circle_at_top,rgba(37,99,235,0.18),transparent_34%),linear-gradient(180deg,#050505,#0b1220)] p-7 lg:border-b-0 lg:border-r lg:p-10">
            <div className="ambient-sweep" />
            <div className="absolute inset-x-8 top-8 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div className="flex items-center justify-between">
                <p className="section-kicker">AI Founder Briefing</p>
                <span className="rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-amber-200" data-testid="aso-video-mocked-badge">
                  Mocked video
                </span>
              </div>

              <div className="mt-7 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-white/[0.52]">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  <Radio className="h-3.5 w-3.5 text-blue-200" />
                  Signal locked
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  <Sparkles className="h-3.5 w-3.5 text-white" />
                  Founder-grade script
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="aso-video-trust-legend">
                {briefingSources.map((item) => (
                  <span key={item} className="rounded-full border border-white/[0.08] bg-black/20 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-white/[0.52]">
                    {item}
                  </span>
                ))}
              </div>

              <div className="mx-auto flex flex-1 items-center justify-center py-8">
                <div className="relative flex h-[21rem] w-[21rem] items-center justify-center rounded-[2.8rem] border border-white/[0.08] bg-white/[0.04] shadow-[0_0_100px_rgba(37,99,235,0.18)]">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 22, ease: "linear", repeat: Infinity }}
                    className="absolute inset-5 rounded-[2.3rem] border border-dashed border-white/[0.12]"
                  />
                  <div className="pulse-ring absolute inset-8 rounded-[2rem] border border-white/[0.08]" />
                  <div className="absolute inset-x-10 top-10 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/[0.44]">
                    <span>Equinox AI Studio</span>
                    <span>{Math.round(progress)}% synced</span>
                  </div>
                  <div className="flex h-48 w-48 items-center justify-center rounded-[2rem] border border-white/[0.08] bg-black/35 text-center backdrop-blur-xl">
                    <div>
                      <p className="font-display text-4xl text-white">EQ</p>
                      <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{report.video_brief.avatar_name}</p>
                      <div className="mt-5 flex items-end justify-center gap-1.5" data-testid="aso-video-waveform-visualizer">
                        {audioBars.map((bar, index) => (
                          <motion.span
                            key={`${bar}-${index}`}
                            className="w-1.5 rounded-full bg-gradient-to-t from-blue-500/50 via-white/80 to-white"
                            animate={{ height: [bar * 0.6, bar, bar * 0.72] }}
                            transition={{ duration: 1.15 + index * 0.08, repeat: Infinity, ease: "easeInOut" }}
                            style={{ height: `${bar}px` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[1.8rem] border border-white/[0.08] bg-black/28 p-5 text-white/75">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-blue-100">
                    <Mic2 className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-[0.22em]">Narration now playing</span>
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/[0.42]" data-testid="aso-video-progress-label">
                    Line {lineIndex + 1} / {totalLines}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.08]">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-white/70 via-blue-400 to-blue-200"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                  />
                </div>
                <div className="mt-5 rounded-[1.4rem] border border-white/[0.06] bg-white/[0.03] p-4">
                  <Mic2 className="h-4 w-4" />
                  <p className="mt-4 text-lg leading-8 text-white/88" data-testid="aso-video-current-line">
                    {report.video_brief.script_lines[lineIndex]}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01))] p-7 lg:p-10">
            <div className="flex items-center gap-3 text-white/72">
              <PlayCircle className="h-5 w-5 text-blue-100" />
              <p className="text-sm uppercase tracking-[0.24em]">Live briefing metrics</p>
            </div>
            <h2 className="mt-5 max-w-[11ch] font-display text-4xl tracking-[-0.05em] text-white">Real-time cards updating while the briefing plays.</h2>
            <p className="mt-5 max-w-2xl text-sm leading-8 text-white/[0.62]">
              This cinematic briefing stays aligned with the report logic below, so founders can absorb the growth story before dropping into the dashboard.
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-white/[0.5]" data-testid="aso-video-trust-copy">
              The briefing is <span className="text-white">MOCKED for now</span>. It references a mix of <span className="text-white">live store inputs</span> and <span className="text-white">estimated Equinox analysis</span> rather than a guaranteed real-time spoken founder video.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {metricCards.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: lineIndex >= index ? 1 : 0.45, y: 0, scale: lineIndex >= index ? 1 : 0.985 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="glitter-card rounded-[1.6rem] border border-white/[0.08] bg-white/[0.04] p-5"
                  data-testid={`aso-video-metric-card-${index + 1}`}
                >
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{item.label}</p>
                  <p className="mt-3 font-display text-3xl text-white">{item.value}</p>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/[0.4]">{item.source}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-[0.68fr_1.32fr]">
              <div className="rounded-[1.8rem] border border-white/[0.08] bg-black/20 p-5" data-testid="aso-video-briefing-meta-card">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Briefing cadence</p>
                <p className="mt-4 font-display text-3xl text-white">{Math.round(progress)}%</p>
                <p className="mt-3 text-sm leading-7 text-white/[0.64]">
                  Founder story, benchmark pressure, and growth opportunity are sequenced to lead smoothly into the action dashboard.
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-blue-100">Estimated narrative layer</p>
              </div>

              <div className="rounded-[1.8rem] border border-white/[0.08] bg-black/20 p-5" data-testid="aso-video-script-panel">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Briefing transcript</p>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Auto-advancing</p>
                </div>
              {report.video_brief.script_lines.map((line, index) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0.4, x: 0 }}
                  animate={{ opacity: index <= lineIndex ? 1 : 0.32, x: index === lineIndex ? 8 : 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className={`flex gap-3 border-b border-white/[0.06] py-3 last:border-none ${index === lineIndex ? "rounded-[1rem] bg-white/[0.03] px-3" : ""}`}
                >
                  <span className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/[0.34]">{String(index + 1).padStart(2, "0")}</span>
                  <p className={`text-sm leading-7 ${index <= lineIndex ? "text-white/82" : "text-white/[0.32]"}`}>{line}</p>
                </motion.div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};